import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { iconForCategory } from '@/components/layout/notification-icons';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { type NotificationItem, useMarkNotificationsReadMutation, usePendingToastsQuery } from '@/services';

import { notificationDetail } from './notification-detail';
import { NotificationToastCard } from './NotificationToastCard';

const VISIBLE = 3;
const LEAVE_MS = 200;

const rank = (item: NotificationItem) => (item.urgency === 'urgent' ? 0 : 1);

/**
 * Top-right stack of urgent and action notifications, rendered straight from the server's
 * pending-toasts query: a card is on screen exactly while its notification is still unread.
 */
export function PersistentNotificationStack() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = usePendingToastsQuery();
  const { mutate: markRead } = useMarkNotificationsReadMutation();
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const items = useMemo(
    () =>
      [...(data?.items ?? [])].sort(
        (a, b) => rank(a) - rank(b) || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [data]
  );

  // Once the server stops listing a dismissed card it no longer needs hiding.
  const stale = [...hidden].filter(id => !items.some(i => i.id === id));
  if (stale.length > 0) setHidden(new Set([...hidden].filter(id => !stale.includes(id))));

  const dismiss = useCallback(
    (ids: string[]) => {
      const without = (set: ReadonlySet<string>) => new Set([...set].filter(id => !ids.includes(id)));
      const timer = setTimeout(() => {
        setHidden(prev => new Set([...prev, ...ids]));
        setLeaving(without);
      }, LEAVE_MS);
      markRead(
        { ids },
        {
          onError: () => {
            clearTimeout(timer);
            setHidden(without);
            setLeaving(without);
          },
        }
      );
      setLeaving(prev => new Set([...prev, ...ids]));
    },
    [markRead]
  );

  const shown = items.filter(i => !hidden.has(i.id));
  if (shown.length === 0) return null;

  const cards = shown.slice(0, VISIBLE);
  const total = Math.max(data?.total ?? 0, shown.length);
  const queued = total - VISIBLE;
  const ghosts = Math.min(2, Math.max(queued, 0));

  return (
    <div className="fixed inset-x-4 top-16 z-50 flex flex-col gap-2 sm:inset-x-auto sm:right-4">
      {cards.map(item => (
        <div
          key={item.id}
          data-testid="persistent-card"
          data-id={item.id}
          className={cn(
            'animate-in fade-in-0 slide-in-from-right-4',
            leaving.has(item.id) && 'animate-out fade-out-0 slide-out-to-right-4'
          )}
        >
          <NotificationToastCard
            tone={item.urgency === 'urgent' ? 'urgent' : 'action'}
            icon={iconForCategory(item.category)}
            title={t(`notifications.item.${item.category}.${item.type}`, item.params)}
            detail={notificationDetail(item, t)}
            time={formatRelativeTime(item.createdAt)}
            onOpen={() => {
              markRead({ ids: [item.id] });
              navigate(item.href);
            }}
            onDismiss={() => dismiss([item.id])}
          />
        </div>
      ))}
      {queued > 0 && (
        <>
          {Array.from({ length: ghosts }, (_, i) => (
            <div
              key={i}
              aria-hidden
              className="pointer-events-none h-3 rounded-xl border bg-card shadow-md"
              style={{
                transform: `translateY(${-(i + 1) * 0.5 - 0.25}rem) scale(${1 - (i + 1) * 0.04})`,
                zIndex: -(i + 1),
              }}
            />
          ))}
          <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
            <span>{t('notifications.more_count', { count: queued })}</span>
            <span aria-hidden>·</span>
            <button
              type="button"
              className="font-medium text-foreground underline-offset-2 hover:underline"
              onClick={() => dismiss(items.map(i => i.id))}
            >
              {t('notifications.dismiss_all')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
